/**
 * 告知函闸门占用与 1003 排队。
 * 催签/催活体以后端 checkSign（noticePhase × payStatus）为准，见 App.vue ensureGates。
 */
import { getSysConf } from '@/api/system'

function parseEnabled(confRes: any): boolean {
  const confStr = confRes?.conf ?? confRes?.data?.conf
  if (!confStr) return false
  try {
    const conf = typeof confStr === 'string' ? JSON.parse(confStr) : confStr
    return !!conf?.enabled
  } catch {
    return false
  }
}

/** @deprecated 旧开关，闸门已改信 checkSign；保留供其它页面兼容读取 */
export async function isInformedConsentEnabled(): Promise<boolean> {
  try {
    const res = await getSysConf('informedConsent')
    return parseEnabled(res)
  } catch {
    return false
  }
}

/** @deprecated 旧开关，闸门已改信 checkSign；保留供其它页面兼容读取 */
export async function isAgreementLivenessEnabled(): Promise<boolean> {
  try {
    const res = await getSysConf('agreementLiveness')
    return parseEnabled(res)
  } catch {
    return false
  }
}

/**
 * 活体/手签闸门占用。
 * 占用期间 1003 缴费提示只暂存，释放后按「活体 → 手签 → 缴费」再弹。
 */
let agreementGateBlocking = false
let pendingUnpaidFeeMessage: string | null = null
let unpaidFeeFlushHandler: ((message: string) => void) | null = null

/** 闸门释放 + 缴费弹框结束后的回调队列 */
let postReleaseCallbacks: Array<() => void> = []
let feeDialogSettledHandler: (() => void) | null = null

/**
 * 注册"所有弹框结束后"的回调（事件驱动，非固定延时）。
 * 弹框链：活体/手签 → 缴费(1003) → 回调
 * 若无闸门阻塞，等 500ms 观察缴费弹框是否出现：
 *   - 出现 → 等其关闭后触发
 *   - 未出现 → 独立触发（不受其他弹框影响）
 */
export function onAfterGatesReleased(cb: () => void) {
  if (!agreementGateBlocking) {
    // 无闸门阻塞，短暂等待缴费弹框是否有待弹出
    setTimeout(() => {
      if ((window as any).__isShowing1003Dialog) {
        // 缴费弹框正在显示 → 等它关闭
        const timer = setInterval(() => {
          if (!(window as any).__isShowing1003Dialog) {
            clearInterval(timer)
            cb()
          }
        }, 300)
      } else {
        // 无缴费弹框 → 独立触发
        cb()
      }
    }, 500)
    return
  }
  postReleaseCallbacks.push(cb)
}

/** 供缴费弹框调用：通知弹框已结束，触发排队回调 */
export function _notifyFeeDialogSettled() {
  feeDialogSettledHandler?.()
}

export function isAgreementGateBlocking(): boolean {
  return agreementGateBlocking
}

export function onFlushUnpaidFeePrompt(handler: (message: string) => void) {
  unpaidFeeFlushHandler = handler
}

export function deferUnpaidFeePrompt(message: string) {
  pendingUnpaidFeeMessage = message || '您有待支付的订单'
}

/** 管理端已标缴费后：丢掉暂存的 1003 文案，避免闸门释放时再弹旧提示 */
export function clearPendingUnpaidFeePrompt() {
  pendingUnpaidFeeMessage = null
}

export function setAgreementGateBlocking(blocking: boolean) {
  agreementGateBlocking = blocking
  if (!blocking && pendingUnpaidFeeMessage) {
    const msg = pendingUnpaidFeeMessage
    pendingUnpaidFeeMessage = null
    // 缴费弹框关闭后触发排队回调
    feeDialogSettledHandler = () => {
      feeDialogSettledHandler = null
      const cbs = postReleaseCallbacks.splice(0)
      cbs.forEach(cb => cb())
    }
    unpaidFeeFlushHandler?.(msg)
  } else if (!blocking) {
    // 无待弹缴费 → 直接触发排队回调
    feeDialogSettledHandler = null
    const cbs = postReleaseCallbacks.splice(0)
    cbs.forEach(cb => cb())
  }
}
